// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — colour system + WCAG contrast helper.
//
// The play surface is a top-down maze drawn on canvas. The colours that carry
// MEANING and must clear the 3:1 graphic floor (principle #22) against the surface
// they sit on (the board floor, the darker wall/border ink, and the DOM panel): the
// WALLS, the MARBLE, the GEMS, the HOLE (+ its warning rim), the EXIT ring, the START
// pad, and the motion TRAIL. Colour is never the only cue — holes are rimmed rings,
// gems are diamonds, the exit is a pulsing ring, the marble has a sheen. Every
// meaningful colour is pinned by contrast.test.ts.

export const PALETTE = {
  // board + panels
  bg: '#101826', // the maze floor / page field (dark sunken corridors)
  board: '#101826',
  wall: '#6b7c93', // maze walls — raised light-steel ridges (>=3:1 vs the dark floor)
  panel: '#16233a',
  panelDeep: '#0e1828',

  // text
  text: '#eef3f8',
  textMuted: '#9fb3c6',
  ink: '#060b12', // dark glyph drawn ON a bright fill

  // play-surface roles (each also has a shape/behaviour cue)
  marble: '#f4b23f', // the amber marble
  sheen: '#ffe3a6', // marble highlight
  gem: '#5fe6d6', // teal gems (diamonds) — kept lighter than the amber marble so the
  //                two are distinct by lightness, not only hue (colour-blind safety)
  hole: '#0a0f18', // the pit
  holeRim: '#ff7a6b', // warning ring around a pit
  exit: '#e56fd0', // magenta exit ring
  start: '#7fd4ff', // start pad
  trail: '#ffd07a', // marble motion trail

  // race field for opponents (each seat also carries its name)
  rival: '#8ea8c4',
} as const;

/* ── WCAG contrast ────────────────────────────────────────────────────────── */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21, symmetric). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
